import { t } from '@/i18n'
import { IDeal, Maybe } from '@/types'
import {
  carDisplayLabel,
  carSubModelDisplayLabel,
  customerDisplayLabel,
} from '@/utils'
import { useApp } from '@/hooks'
import { useMemo } from 'react'
import { GeneralSettingsObjectType } from '@/apollo/graphql'
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/constants'
import moment from 'moment'

export const getDealFinancialBuildData = (
  dealData: Partial<IDeal>,
  generalSettings: Maybe<GeneralSettingsObjectType>,
) => [
  {
    type: 'text',
    className: 'w-50',
    name: 'baseIndex',
    label: 'base_index',
    value: dealData?.baseIndex,
  },
  {
    type: 'text',
    className: 'w-50',
    name: 'current_index',
    editable: false,
    label: 'current_index',
    value: generalSettings?.index,
  },
  {
    type: 'text',
    name: 'carPrice',
    className: 'w-50',
    label: 'car_price',
    value: t('currency', {
      value: dealData?.carPrice || 0,
      currency: dealData?.currency,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'licence_price',
    name: 'licence_price',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.licencePrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'required_insurance',
    name: 'compulsoryInsurancePrice',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.compulsoryInsurancePrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'full_insurance_price',
    name: 'comprehensiveInsurancePrice',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.comprehensiveInsurancePrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'young_drive_fee',
    name: 'comprehensiveInsuranceYoungPrice',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.comprehensiveInsuranceYoungPrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'full_monthly_payment',
    name: 'fullMonthlyPrice',
    editable: false,
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.fullMonthlyPrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    name: 'service_price',
    label: 'maintenance_price',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.servicePrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'buy_option',
    name: 'residualPrice',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.residualPrice || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'trackingMonthlyRate',
    name: 'trackingMonthlyRate',
    value: t('currency', {
      currency: dealData?.currency,
      value: dealData?.trackingMonthlyRate || 0,
    }),
  },
  {
    type: 'text',
    className: 'w-50',
    label: 'npv',
    name: 'npv',
    value: dealData?.npv,
  },
]

export const useBuildData = (dealData: Partial<IDeal>, color?: string) => {
  const { isRTL, generalSettings } = useApp()

  return useMemo(
    () => ({
      general_info: [
        {
          name: 'id',
          label: 'id',
          type: 'text',
          editable: false,
          className: 'w-50',
          value: dealData?.id,
        },
        {
          label: 'status',
          style: { color },
          value: t(dealData?.status || ''),
        },
        {
          editable: false,
          type: 'text',
          value: dealData?.dealTypeString,
          name: 'deal_type',
          className: 'w-50',
          label: 'deal_type',
        },
        {
          label: 'start_time',
          value: moment(dealData?.startDate).format(DATE_FORMAT),
        },
        {
          label: 'end_time',
          value: moment(dealData?.endDate).format(DATE_FORMAT),
        },
        {
          label: 'customer',
          value: customerDisplayLabel(dealData?.customer),
          link: `/${dealData?.customer?.id}/main`,
        },
        {
          label: 'sub_model',
          value: dealData?.carSubModel?.localName,
        },
        {
          label: 'car_model',
          value: dealData?.carSubModel?.carModel.name,
        },
        {
          label: 'brand',
          value: dealData?.carSubModel?.carModel?.brand?.name,
        },
        {
          label: 'car',
          value: dealData?.car && carDisplayLabel(dealData?.car, isRTL),
          link: dealData?.car
            ? `/${dealData?.customer?.id}/cars/${dealData?.car?.id}`
            : null,
        },
        {
          label: 'notes',
          value: dealData?.notes,
          className: 'w-50',
          type: 'text',
          name: 'notes',
        },
      ],
      financial_details: getDealFinancialBuildData(dealData, generalSettings),
    }),
    [dealData, color, isRTL, generalSettings],
  )
}
